import { useId, useMemo } from "react"
import { type TIntegration } from "./store"

export function PlatformIcon({
	platform,
	size = "24px",
}: {
	platform: TIntegration["platform"]
	size?: string
}) {
	switch (platform) {
		case "google_ads":
			return <GoogleAds size={size} />
		case "meta":
			return <Meta size={size} />
		case "pg_payu":
			return <PayU size={size} />
		case "pg_simulator":
			return <PGSimulator size={size} />
		case "api":
			return <API size={size} />
		default:
			break
	}
	return null
}

function GoogleAds({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			version="1.1"
			width={size}
			height={size}
			viewBox="0 0 192 192"
		>
			<rect
				x="8"
				y="62.52"
				transform="matrix(0.5 -0.866 0.866 0.5 -46.2127 103.666)"
				fill="#FBBC04"
				width="117.33"
				height="58.67"
			/>
			<path
				fill="#4285F4"
				d="M180.07,127.99L121.4,26.38c-8.1-14.03-26.04-18.84-40.07-10.74c-14.03,8.1-18.84,26.04-10.74,40.07   l58.67,101.61c8.1,14.03,26.04,18.83,40.07,10.74C183.36,159.96,188.16,142.02,180.07,127.99z"
			/>
			<circle fill="#34A853" cx="37.34" cy="142.66" r="29.33" />
		</svg>
	)
}

function Meta({ size = "24px" }: { size?: string }) {
	const id = useId()
	const width = size
	const height = useMemo(() => {
		const original_width = 72
		const original_height = 48
		const aspectRatio = original_width / original_height
		return (
			String(Math.ceil(Number(size.replace("px", "")) / aspectRatio)) + "px"
		)
	}, [size])
	return (
		<svg
			aria-label="Meta symbol"
			height={height}
			width={width}
			role="img"
			viewBox="0 0 150 100"
		>
			<defs>
				<linearGradient
					gradientUnits="userSpaceOnUse"
					id={`meta_logo_${id}_0`}
					x1="124.599"
					x2="159.703"
					y1="96.689"
					y2="57.25"
				>
					<stop offset="0.21" stopColor="#0278F1"></stop>
					<stop offset="0.533" stopColor="#0180FA"></stop>
				</linearGradient>
				<linearGradient
					gradientUnits="userSpaceOnUse"
					id={`meta_logo_${id}_1`}
					x1="43.859"
					x2="0"
					y1="4.984"
					y2="63.795"
				>
					<stop offset="0.427" stopColor="#0165E0"></stop>
					<stop offset="0.917" stopColor="#0180FA"></stop>
				</linearGradient>
				<linearGradient
					gradientUnits="userSpaceOnUse"
					id={`meta_logo_${id}_2`}
					x1="28.409"
					x2="134.567"
					y1="28.907"
					y2="71.769"
				>
					<stop stopColor="#0064E0"></stop>
					<stop offset="0.656" stopColor="#0066E2"></stop>
					<stop offset="1" stopColor="#0278F1"></stop>
				</linearGradient>
			</defs>
			<path
				d="M107.654 0c-12.3 0-21.915 9.264-30.618 21.032C65.076 5.802 55.073 0 43.103 0 18.698 0 0 31.76 0 65.376 0 86.41 10.177 99.679 27.223 99.679c12.268 0 21.092-5.784 36.778-33.203 0 0 6.539-11.547 11.037-19.501a514.1 514.1 0 0 1 4.98 8.227l7.356 12.374c14.329 23.977 22.312 32.103 36.778 32.103C140.758 99.68 150 86.23 150 64.757 150 29.56 130.88 0 107.654 0ZM52.039 59.051C39.322 78.987 34.922 83.455 27.841 83.455c-7.287 0-11.617-6.397-11.617-17.804 0-24.404 12.167-49.359 26.672-49.359 7.855 0 14.42 4.537 24.474 18.93-9.547 14.645-15.33 23.83-15.33 23.83Zm47.999-2.51-8.795-14.667c-2.38-3.87-4.66-7.428-6.862-10.689 7.927-12.234 14.465-18.33 22.241-18.33 16.155 0 29.079 23.786 29.079 53.002 0 11.137-3.647 17.599-11.205 17.599-7.244 0-10.704-4.785-24.458-26.914Z"
				fill="#0180FA"
			></path>
			<path
				d="M145.119 34.888h-14.878c3.44 8.718 5.46 19.42 5.46 30.969 0 11.137-3.647 17.599-11.205 17.599-1.403 0-2.663-.18-3.884-.63V99.5a33.48 33.48 0 0 0 3.54.18C140.758 99.68 150 86.23 150 64.756c0-10.702-1.768-20.882-4.881-29.87Z"
				fill={`url(#meta_logo_${id}_0)`}
			></path>
			<path
				d="M43.103 0c.253 0 .505.003.756.008v16.308c-.319-.016-.64-.024-.962-.024-14.138 0-26.055 23.706-26.65 47.503H.014C.588 30.77 19.08 0 43.103 0Z"
				fill={`url(#meta_logo_${id}_1)`}
			></path>
			<path
				d="M43.103 0c11.115 0 20.534 5.004 31.4 17.913 3.055 3.817 6.74 8.777 10.682 14.511l.017.025.024-.003c1.939 2.912 3.94 6.05 6.017 9.428l8.795 14.668c13.754 22.13 17.214 26.913 24.458 26.913h.103v16.22c-.148.003-.297.004-.447.004-14.466 0-22.449-8.126-36.778-32.103l-7.356-12.374a587.065 587.065 0 0 0-2.287-3.822l.019-.045c-5.483-8.971-8.971-14.454-11.962-18.44l-.05.037c-9.15-12.59-15.42-16.64-22.842-16.64h-.034V.001l.24-.001Z"
				fill={`url(#meta_logo_${id}_2)`}
			></path>
		</svg>
	)
}

function API({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			width={size}
			height={size}
		>
			<path
				fillRule="evenodd"
				d="M3 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6Zm14.25 6a.75.75 0 0 1-.22.53l-2.25 2.25a.75.75 0 1 1-1.06-1.06L15.44 12l-1.72-1.72a.75.75 0 1 1 1.06-1.06l2.25 2.25c.141.14.22.331.22.53Zm-10.28-.53a.75.75 0 0 0 0 1.06l2.25 2.25a.75.75 0 1 0 1.06-1.06L8.56 12l1.72-1.72a.75.75 0 1 0-1.06-1.06l-2.25 2.25Z"
				clipRule="evenodd"
			/>
		</svg>
	)
}

function PayU({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			id="Layer_1"
			data-name="Layer 1"
			viewBox="0 0 514.56 280.44"
			width={size}
			height={size}
		>
			<path
				className="cls-1"
				fill="#414042"
				strokeWidth="0"
				d="M0,97.77h25.72v12.86c2.93-4.46,7.37-8.1,13.3-10.92,5.93-2.82,12.3-4.23,19.11-4.23,8.1,0,15.56,2.06,22.37,6.17,6.81,4.11,12.27,9.98,16.38,17.62,4.11,7.64,6.16,16.56,6.16,26.78s-2.06,19.29-6.16,26.86c-4.11,7.58-9.6,13.36-16.47,17.35-6.87,3.99-14.3,5.99-22.28,5.99-6.93,0-13.36-1.38-19.29-4.14-5.93-2.76-10.31-6.37-13.12-10.83v99.18H0V97.77ZM69.67,165.94c4.76-5.17,7.13-11.8,7.13-19.9s-2.38-14.94-7.13-20.17c-4.76-5.22-10.89-7.84-18.41-7.84s-13.68,2.61-18.5,7.84c-4.82,5.23-7.22,11.95-7.22,20.17s2.41,14.74,7.22,19.9c4.81,5.17,10.98,7.75,18.5,7.75s13.65-2.58,18.41-7.75ZM134.67,190.25c-6.87-3.99-12.36-9.78-16.47-17.35-4.11-7.58-6.17-16.53-6.17-26.86s2.05-19.14,6.17-26.78c4.11-7.63,9.57-13.5,16.38-17.62,6.81-4.11,14.27-6.17,22.37-6.17,6.81,0,13.18,1.41,19.11,4.23,5.93,2.82,10.36,6.46,13.3,10.92v-12.33h25.72v95.13h-25.72v-12.15c-2.82,4.46-7.2,8.07-13.12,10.83-5.93,2.76-12.36,4.14-19.29,4.14-7.99,0-15.41-2-22.28-5.99ZM182.41,165.94c4.76-5.17,7.14-11.8,7.14-19.9s-2.38-14.94-7.14-20.17c-4.76-5.22-10.89-7.84-18.41-7.84s-13.68,2.61-18.5,7.84c-4.82,5.23-7.22,11.95-7.22,20.17s2.41,14.74,7.22,19.9c4.81,5.17,10.98,7.75,18.5,7.75s13.65-2.58,18.41-7.75ZM272.69,196.24c-14.56,0-25.31-3.7-32.24-11.1-6.93-7.4-10.39-17.73-10.39-31v-56.37h26.07v53.73c0,7.4,1.94,12.98,5.81,16.73,3.88,3.76,9.22,5.64,16.03,5.64,6.22,0,11.6-2.05,16.12-6.17,4.52-4.11,6.78-9.69,6.78-16.73v-53.2h25.72v182.68h-25.72v-96.71c-6.11,8.34-15.5,12.51-28.19,12.51ZM348.44,252.44l142.86-43.16V75.57l-164.71-41.93v40.87h-25.72V0l213.68,56.19v171.75l-166.12,51.62v-27.13Z"
			/>
			<path
				className="cls-2"
				fill="#00ad7d"
				d="M362.18,97.77h26.07v53.73c0,15.03,7.1,22.55,21.32,22.55s21.32-7.51,21.32-22.55v-53.73h26.07v56.37c0,13.15-4.05,23.46-12.15,30.91-8.1,7.46-19.85,11.19-35.23,11.19s-27.13-3.76-35.23-11.27c-8.1-7.52-12.16-17.79-12.16-30.83v-56.37Z"
			/>
		</svg>
	)
}

function PGSimulator({ size = "24px" }: { size?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2" />
			<path d="M6.453 15h11.094" />
			<path d="M8.5 2h7" />
		</svg>
	)
}
